"use client";

import { useState, useRef, useEffect } from "react";
import { WoozooTextLogo } from "./WoozooLogo";
import { useI18n } from "@/i18n/context";
import { localeLabels, type Locale } from "@/i18n/translations";

const locales = Object.keys(localeLabels) as Locale[];

export function Header() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-[60px] py-[33px]">
      <div className="flex items-center justify-between max-w-[1440px] mx-auto">
        {/* Logo */}
        <a href="#home" aria-label="WooZoo Home">
          <WoozooTextLogo className="w-[196px] h-[34px]" />
        </a>

        {/* Right side: lang + CTA */}
        <div className="flex items-center gap-[10px]">
          {/* Language selector */}
          <div ref={ref} className="relative">
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center font-dm font-medium text-[15px] text-text-secondary uppercase tracking-[1.5px] hover:text-text-primary transition-colors"
              aria-expanded={open}
              aria-haspopup="listbox"
            >
              {localeLabels[locale]}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className={`ml-[2px] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            </button>

            {/* Dropdown */}
            {open && (
              <div
                className="absolute top-full right-0 mt-[4px] bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15)] min-w-[60px] py-[4px]"
                role="listbox"
              >
                {locales.map((l) => (
                  <button
                    key={l}
                    role="option"
                    aria-selected={l === locale}
                    onClick={() => {
                      setLocale(l);
                      setOpen(false);
                    }}
                    className={`block w-full px-[12px] py-[6px] text-left font-dm font-medium text-[13px] uppercase tracking-[1.5px] transition-colors ${
                      l === locale
                        ? "text-text-primary bg-teal-light/30"
                        : "text-text-secondary hover:text-text-primary hover:bg-bg-primary"
                    }`}
                  >
                    {localeLabels[l]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Business CTA */}
          <a
            href="#contact"
            className="bg-bg-dark flex items-center justify-center gap-[3px] h-[43px] px-[12px] py-[8px] hover:opacity-90 transition-opacity"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className="shrink-0"
            >
              <path
                d="M2.7 10L2 9.3L7.8 3.5H4.5V2.5H9.5V7.5H8.5V4.2L2.7 10Z"
                fill="white"
              />
            </svg>
            <span className="font-jetbrains text-[15px] text-white uppercase tracking-[1.5px] text-center">
              {t.header.business}
            </span>
          </a>
        </div>
      </div>
    </header>
  );
}
